// src/features/arrivals/components/ArrivalsPage.tsx
//
// «Llegadas de hoy» (mockup 01) — the controller screen under Recepción,
// route `/arrivals`. Scope selector in the header, Hoy/Mañana toggle,
// «Solo pendientes», a small counters row (NOT KpiHero — this is a list, not
// a dashboard), the live strip per channel, the attention-first table, and a
// side-panel detail drawer (opens the reservation detail of unit C).
import React, { useState } from 'react';
import {
  Box,
  Button,
  Divider,
  Drawer,
  Paper,
  useMediaQuery,
  useTheme,
  FormControlLabel,
  IconButton,
  Switch,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import { CloseRounded as CloseIcon, DesktopWindowsRounded as WallIcon } from '@mui/icons-material';
import { useTranslate } from 'react-admin';
import { SectionHeader } from '@/components/common/SectionHeader';
import { FetchErrorBanner } from '@/components/common/FetchErrorBanner';
import { HotelScopeSelector } from '@/components/common/HotelScopeSelector';
import { useHotelScope } from '@/hooks/useHotelScope';
import { useArrivals } from '@/hooks/useArrivals';
import { useCsvExport } from '@/hooks/useCsvExport';
import { hasResourcePermission } from '@/config/resourceConfig';
import { stashDeepLink } from '@/utils/deepLink';
import { displayIdentifier } from '@/features/reservations/lib/reservationFormat';
import { ReservationDetailContent } from '@/features/reservations/components/ReservationDetailContent';
import { LiveStrip } from './LiveStrip';
import { ArrivalsTable } from './ArrivalsTable';
import { singleScopeHotelId } from '../lib/singleScopeHotel';
import { OperatorCodeButton } from '@/features/devices/components/OperatorCodeButton';
import type { ReservationState } from '@/types/reservationState';

/** One figure of «Estado de las llegadas»: the number leads, the label is secondary. */
const StatTile: React.FC<{ label: string; value: number; hint?: string; tone?: 'error' }> = ({ label, value, hint, tone }) => {
  const tile = (
    <Box sx={{ minWidth: 0 }}>
      <Typography sx={{ fontSize: '1.375rem', fontWeight: 700, lineHeight: 1.2, color: tone === 'error' ? 'error.main' : 'text.primary' }}>
        {value}
      </Typography>
      <Typography variant="caption" sx={{ display: 'block', lineHeight: 1.3, color: tone === 'error' ? 'error.main' : 'text.secondary' }}>
        {label}
      </Typography>
    </Box>
  );
  return hint ? <Tooltip title={hint}>{tile}</Tooltip> : tile;
};

export const ArrivalsPage: React.FC = () => {
  const translate = useTranslate();
  const scope = useHotelScope();
  const isPhone = useMediaQuery(useTheme().breakpoints.down('sm'));

  const [dateMode, setDateMode] = useState<'today' | 'tomorrow'>('today');
  const [pendingOnly, setPendingOnly] = useState(false);
  const [selected, setSelected] = useState<ReservationState | null>(null);

  // «Hoy»/«Mañana» are each establishment's own business day (its time zone and day-closing
  // hour), resolved by the server: the browser's or UTC's calendar would be wrong around midnight.
  const { data, loading, error, secondsSinceUpdate, refresh, acknowledge, pin } = useArrivals({
    day: dateMode,
    pendingOnly,
    ...scope.scopeParams,
  });

  const exportCsv = useCsvExport({ page: 'arrivals' });
  const canExport = hasResourcePermission('arrivals', 'export');

  const handleExport = async () => {
    if (!data) return;
    // The shared date normaliser may turn `2026-10-01` into a full ISO instant: keep the day.
    const date = String(data.date).slice(0, 10);
    await exportCsv(data.arrivals, [
      { labelKey: 'resources.arrivals.columns.guest', value: (r: ReservationState) => displayIdentifier(r) },
      { labelKey: 'resources.arrivals.columns.room', value: (r: ReservationState) => r.roomNumber ?? '' },
      { labelKey: 'resources.arrivals.columns.state', value: (r: ReservationState) => translate(`resources.reservationStates.${r.state}`) },
    ], { from: date, to: date });
  };

  // The wall screen shows one hotel: offered whenever the scope in effect is exactly one, whether
  // picked in the selector or because the user only has one (they never see a selector).
  const singleHotelId = singleScopeHotelId(scope.selectedHotelIds, scope.allHotels);
  const wallRoute = singleHotelId ? `/panel?hotel=${encodeURIComponent(singleHotelId)}` : undefined;
  // A new tab cold-boots the app and the sign-on redirect drops the hash route,
  // so the destination is handed off and replayed once the app has mounted
  // (same mechanism as the session-detail links).
  const openWall = () => {
    if (!wallRoute) return;
    stashDeepLink(wallRoute);
    window.open(`${window.location.origin}/`, '_blank', 'noopener');
  };

  return (
    <Box>
      <SectionHeader manualScreen="arrivals"
        sx={{ px: { xs: 2, sm: 3 } }}
        left={
          <Box sx={{ display: 'flex', alignItems: 'center', columnGap: 1.5, rowGap: 0.75, flexWrap: 'wrap' }}>
            <HotelScopeSelector scope={scope} />
            <ToggleButtonGroup
              size="small"
              exclusive
              value={dateMode}
              onChange={(_, v) => v && setDateMode(v)}
              sx={{ '& .MuiToggleButton-root': { fontSize: '0.75rem' } }}
            >
              <ToggleButton value="today" sx={{ textTransform: 'none', fontWeight: 600, px: 1.5 }}>
                {translate('resources.arrivals.tabs.today')}
              </ToggleButton>
              <ToggleButton value="tomorrow" sx={{ textTransform: 'none', fontWeight: 600, px: 1.5 }}>
                {translate('resources.arrivals.tabs.tomorrow')}
              </ToggleButton>
            </ToggleButtonGroup>
            <FormControlLabel
              control={<Switch size="small" checked={pendingOnly} onChange={(e) => setPendingOnly(e.target.checked)} />}
              label={translate('resources.arrivals.pendingOnly')}
              sx={{ ml: 0, mr: 0, '& .MuiFormControlLabel-label': { fontSize: '0.8125rem' } }}
            />
          </Box>
        }
        helpConfig={{
          titleKey: 'resources.arrivals.help.title',
          descriptionKey: 'resources.arrivals.help.description',
          featureKeys: [
            'resources.arrivals.help.features.liveStrip',
            'resources.arrivals.help.features.sortOrder',
            'resources.arrivals.help.features.actions',
          ],
          tipKeys: [
            'resources.arrivals.help.tips.pendingOnly',
            'resources.arrivals.help.tips.wallScreen',
            'resources.arrivals.help.tips.operatorCode',
          ],
        }}
        showExport={canExport}
        onExportClick={handleExport}
        onRefresh={refresh}
        refreshing={loading}
        extraActions={
          <Typography variant="caption" color="text.secondary" noWrap sx={{ pr: 0.5 }}>
            {translate('resources.arrivals.updatedAgo', { seconds: secondsSinceUpdate })}
          </Typography>
        }
        rightActions={
          <>
            {/* Shown (disabled, with the reason) when the scope is not exactly one hotel. */}
            <Tooltip title={wallRoute ? '' : translate('resources.arrivals.wallScreenNeedsHotel')}>
              <span>
                {isPhone ? (
                  <IconButton
                    disabled={!wallRoute}
                    onClick={openWall}
                    aria-label={translate('resources.arrivals.wallScreen')}
                    sx={{ border: 1, borderColor: 'divider', borderRadius: 1, backgroundColor: 'background.paper', width: 40, height: 40 }}
                  >
                    <WallIcon sx={{ fontSize: 18 }} />
                  </IconButton>
                ) : (
                  <Button variant="outlined" disabled={!wallRoute} startIcon={<WallIcon />} onClick={openWall} sx={{ textTransform: 'none', fontWeight: 600 }}>
                    {translate('resources.arrivals.wallScreen')}
                  </Button>
                )}
              </span>
            </Tooltip>
            <OperatorCodeButton hotelIds={scope.selectedHotelIds ?? (singleHotelId ? [singleHotelId] : undefined)} />
          </>
        }
      />

      <Box sx={{ px: { xs: 2, sm: 3 }, pb: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
        {error && <FetchErrorBanner onRetry={refresh} />}

        {data && (
          <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
            <LiveStrip bare groups={data.live} onSelect={(session) => setSelected(session)} />
            <Divider />
            <Box>
              <Typography variant="overline" sx={{ display: 'block', mb: 1 }}>
                {translate('resources.arrivals.counters.title')}
              </Typography>
              <Box sx={{ display: 'grid', gridTemplateColumns: { xs: 'repeat(2, minmax(0, 1fr))', sm: 'repeat(auto-fit, minmax(140px, 1fr))' }, gap: 2 }}>
                <StatTile
                  label={data.knownOnly ? translate('resources.arrivals.counters.arrivalsKnownShort') : translate('resources.arrivals.counters.arrivals')}
                  value={data.counters.arrivals}
                  hint={data.knownOnly ? translate('resources.arrivals.counters.arrivalsKnownHint') : undefined}
                />
                <StatTile label={translate('resources.arrivals.counters.ready')} value={data.counters.ready} />
                <StatTile label={translate('resources.arrivals.counters.preCheckInPartial')} value={data.counters.preCheckInPartial} />
                <StatTile label={translate('resources.arrivals.counters.notStarted')} value={data.counters.notStarted} />
                <StatTile label={translate('resources.arrivals.counters.checkedIn')} value={data.counters.checkedIn} />
                {data.counters.attention > 0 && (
                  <StatTile label={translate('resources.arrivals.counters.attention')} value={data.counters.attention} tone="error" />
                )}
              </Box>
            </Box>
          </Paper>
        )}

        <ArrivalsTable
          rows={data?.arrivals ?? []}
          loading={loading}
          onRowClick={(row) => setSelected(row)}
          onAcknowledge={(id) => void acknowledge(id)}
          onPin={(id, pinned) => void pin(id, pinned)}
          pendingOnly={pendingOnly}
          onClearPendingOnly={() => setPendingOnly(false)}
        />
      </Box>

      <Drawer anchor="right" open={Boolean(selected)} onClose={() => setSelected(null)}>
        <Box sx={{ width: { xs: '100vw', sm: 480 }, p: { xs: 2, sm: 3 } }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" fontWeight={700}>{translate('resources.arrivals.detail.title')}</Typography>
            <IconButton size="small" onClick={() => setSelected(null)}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
          {selected && <ReservationDetailContent reservationId={selected.id} linkToFull />}
        </Box>
      </Drawer>
    </Box>
  );
};

export default ArrivalsPage;
